// COTELE PLANULUI DE NIVEL — lanțurile pe care le citește cineva care verifică.
//
// „cotele m-ar ajuta foarte tare la debug pe mine": un plan fără cote se poate
// uita corect și fi greșit, iar o travee de 6,10 m desenată în locul uneia de
// 6,00 arată exact la fel până când o măsoară cineva. Astea sunt măsurate din
// geometria documentului, nu citite de pe desen.
//
// DE CE `DimSpan`, ȘI NU `structure/cote.tsx`
// ---------------------------------------------------------------------------
// Există deja două vocabulare de cotare în platformă, și niciunul nu e „cel
// mai bun" — sunt pentru desene diferite:
//
//   `structure/cote.tsx`  PIXELI, pentru secțiunea unui element (grindă, stâlp,
//                         ochi de placă). Are pragma lui de JSX și martori
//                         punctați; e desenul unei piese, la scara ei.
//   `siteGlyphs.DimSpan`  METRI de desen, pentru planșa de situație. Aceeași
//                         pânză, aceeași transformare scale-only, și trece deja
//                         prin exportul PDF (svg2pdf.js, care nu implementează
//                         `non-scaling-stroke` sau `paint-order`).
//
// Planul de nivel e al doilea caz: metri, aceeași pânză, același export. Iar
// două desene din ACELAȘI vizualizator care s-ar cota diferit — unul în metri
// cu virgulă, altul în centimetri — ar pune cititorul să întrebe de fiecare
// dată în ce unitate se uită. Deci se cotează ca planul de situație: `cm`, fără
// sufix, cum scriu planșele de referință.

import type { StoreyPlan, StoreyShape } from "../canonical";
import { cm, type DimSpan } from "./siteDerive";




/** Două poziții mai apropiate de-atât sunt ACEEAȘI poziție. Stâlpii unui șir nu
 *  cad la milimetru pe aceeași axă — nici în document, nici pe planșă — iar un
 *  lanț care ar socoti fiecare abatere de 3 mm drept deschidere ar scrie zeci
 *  de cote de „0" și ar ascunde exact deschiderea pe care cineva o caută. */
const TOL_M = 0.06;

/** Centrul unui obiect: cel declarat, altfel media inelului. Un stâlp generat
 *  vine ca `cx/cy`, unul importat ca inel — v. nota despre cele două emiteri
 *  din `canonical.ts`. */
function centrul(s: StoreyShape): [number, number] | null {
  if (s.cx != null && s.cy != null) return [s.cx, s.cy];
  if (s.poly.length < 3) return null;
  const n = s.poly.length;
  let [sx, sy] = [0, 0];
  for (const [x, y] of s.poly) { sx += x; sy += y; }
  return [sx / n, sy / n];
}

/** Pozițiile distincte de pe o axă, sortate și strânse cu `TOL_M`.
 *
 *  Un grup strâns întoarce MEDIA lui, nu primul element. Primul ar fi făcut
 *  rezultatul să depindă de ordinea în care documentul scrie stâlpii — aceeași
 *  clădire, două cote, după cum cade sortarea — iar media e pur și simplu cea
 *  mai bună estimare a axei pe care stau toți.
 *
 *  Ce NU face: nu rotunjește la o cifră „frumoasă". Doi stâlpi la 5,998 m
 *  rămân la 5,998 și cota scrie 599,8 — dacă modelul are 2 mm de abatere, asta
 *  se vede. Un lanț care ar scrie 600 fiindcă „e evident 6 m" ar fi exact
 *  desenul care ascunde ce e stratul ăsta pus să arate. */
function pozitii(valori: number[]): { la: number; cati: number }[] {
  const sortate = [...valori].sort((a, b) => a - b);
  const out: { la: number; cati: number }[] = [];
  let grup: number[] = [];
  const inchide = () => {
    if (grup.length) {
      out.push({ la: grup.reduce((t, v) => t + v, 0) / grup.length,
                 cati: grup.length });
    }
    grup = [];
  };
  for (const v of sortate) {
    if (grup.length && v - grup[grup.length - 1] > TOL_M) inchide();
    grup.push(v);
  }
  inchide();
  return out;
}

/** Câți stâlpi trebuie să stea pe aceeași axă ca aia să fie un ALINIAMENT.
 *
 *  Doi. Unul singur e un stâlp, nu o axă — și tocmai stâlpii singuratici erau
 *  cei care rupeau lanțul: pe subsolul documentului real, din 14 poziții pe X,
 *  șase erau ale unui singur stâlp, iar între ele ieșeau cote de 37 și 75 cm
 *  care nu însemnau nimic. Un lanț cu numere care nu înseamnă nimic e mai rău
 *  decât niciun lanț: cine îl citește crede că le-a măsurat cineva. */
const MIN_ALINIERE = 2;


/** Ce se cotează, pe straturi — după convenția planșei de referință (A07).
 *
 *  PERIMETRUL, în trei rânduri drepte pe fiecare latură, dinspre clădire spre
 *  exterior. Așa cotează planșa, și de aia arată altfel decât ce era aici:
 *
 *    `fatada`  — rândul lipit de clădire: lanțul plin–gol–plin de-a lungul
 *                fațadei. Cel care spune lățimea unei ferestre și cât zid
 *                rămâne lângă ea.
 *    `axe`     — rândul din mijloc: între aliniamentele de stâlpi. Pe A07 e
 *                „ax la ax"; documentul nostru n-are obiecte `Axis` (0 din
 *                23 864), deci aliniamentele sunt tot ce se poate spune cinstit.
 *    `total`   — rândul cel mai depărtat: o singură cotă pe toată latura.
 *
 *  GABARITUL NU MAI E UN DESEN APARTE. Era o pereche de linii trase între
 *  colțurile bounding-box-ului pe AXELE LUMII — iar clădirea stă strâmb, deci
 *  cotele ieșeau oblice și măsurau plicul rotit, nu clădirea: 7825,6 în loc de
 *  7530 pe corpul C3. Pe planșă totalul nu e o linie separată, ci ultimul rând
 *  al benzii, drept, paralel cu fațada. Aici e la fel.
 *
 *  INTERIORUL, în negru, fără bandă — ca pe A07:
 *
 *    `camere`  — lumina fiecărei camere, pe direcția camerei.
 *    `iesiri`  — lumina ieșirilor de salvare ale adăposturilor ALA (săritura
 *                de lup, culoarul ei): golul de 130 × 115 dintre pereți, cel pe
 *                care îl cere normativul. Separate de `camere` fiindcă se cer
 *                pe planșa de parcaj de fiecare dată când se aprind cotele
 *                (Oana, 28.09.2026), pe când lumina camerelor stă stinsă.
 *    `goluri`  — lanțurile plin–gol–plin de-a lungul pereților DINĂUNTRU.
 *                Pereții de fațadă nu mai intră aici: pe ei se uită banda
 *                `fatada`, iar desenați de două ori erau jumătate din
 *                înghesuiala de pe ecran. */
export interface CoteNivel {
  fatada: DimSpan[];
  axe: DimSpan[];
  total: DimSpan[];
  camere: DimSpan[];
  iesiri: DimSpan[];
  goluri: DimSpan[];
  /** ADĂPOSTURILE ALA (Oana, 30.09.2026): lumina SAS-ului și a grupului
   *  sanitar, lanțurile plin–gol–plin pe pereții adăpostului (lățimea ușilor
   *  UME, a ușii băii, a oblonului) și distanțele dintre pereți și elementele
   *  verticale dinăuntru — stâlpi, nuclee, SAS, baie — pe direcțiile
   *  adăpostului. Separat de `camere` și `goluri`: adăpostul e transparent și
   *  neregulat, lumina lui n-ar spune nimic, iar pereții lui stau adesea pe
   *  fațadă, unde `goluri` nu se uită. */
  ala: DimSpan[];
  /** HOLURILE CĂILOR DE EVACUARE (Oana, 20.10.2026), desenate odată cu
   *  cotele, fără bifă a lor, ca `iesiri`: lumina holului SAS
   *  (`architecture/evacuare/pas.py`, camera de fel `sas` cu
   *  `architecture.evacuare`) — lățimea liberă de 0,90 și lungimile fiecărei
   *  bucăți drepte, pe fețele pereților lui. Ușile lui, cu plinurile de lângă
   *  ele, le dă `goluri`: pereții holului sunt pereți interiori ca oricare. */
  evacuare: DimSpan[];
}

export const CoteGoale: CoteNivel =
  { fatada: [], axe: [], total: [], camere: [], iesiri: [], goluri: [], ala: [], evacuare: [] };

/** Holul SAS al unei căi de evacuare — v. `CoteNivel.evacuare`. */
const eHolEvacuare = (r: StoreyShape) => r.kind === "sas" && !!r.evacuare;

/** O cameră a unui adăpost ALA, fără ieșirea de salvare (care are cotele ei). */
const eCameraAla = (r: StoreyShape) =>
  (r.kind ?? "").startsWith("ala") && r.kind !== "ala_iesire";

/** Ieșirea de salvare a unui adăpost ALA — v. `CoteNivel.iesiri`. */
const eIesireAla = (r: StoreyShape) => r.kind === "ala_iesire";

/** Grosimea pereților săriturii de lup, citită de pe pereții ei (`rol`
 *  «saritura», lângă golul ei); 0,40 când nu-i găsește. */
const PERETE_PUT_M = 0.4;
function grosimeaPutului(r: StoreyShape, pereti: StoreyShape[]): number {
  if (r.poly.length < 3) return PERETE_PUT_M;
  const cx = r.poly.reduce((s, p) => s + p[0], 0) / r.poly.length;
  const cy = r.poly.reduce((s, p) => s + p[1], 0) / r.poly.length;
  for (const w of pereti) {
    const ax = w.axis;
    if (!ax || ax.length < 2 || !w.thickness_m) continue;
    const [a, b] = [ax[0], ax[ax.length - 1]];
    if (Math.hypot((a[0] + b[0]) / 2 - cx, (a[1] + b[1]) / 2 - cy) < 2.0) return w.thickness_m;
  }
  return PERETE_PUT_M;
}

/** Cotele unei sărituri de lup, cum le pune planșa de referință a Oanei
 *  (28.09.2026): NU prin centrul golului, unde s-ar călca cu numele, ci
 *  defazate — lanțul «40 / 130 / 40» în lungul fațadei, spre fundul puțului;
 *  adâncimea golului, lângă un perete lateral; și totalul cu pereți («210»),
 *  puțin în afara fundului. `spre` (din `props["architecture.ala"]`) e sensul
 *  ieșirii, dinspre adăpost spre exterior; fără el, cotele de cameră. */
function coteleIesirii(r: StoreyShape, e: number): DimSpan[] {
  const s = r.ala?.spre;
  const L = s && s.length >= 2 ? Math.hypot(s[0], s[1]) : 0;
  if (!s || !(L > 0) || r.poly.length < 3) return coteleCamerei(r);
  // Axele sunt ALE GOLULUI (laturile lui), nu ale lui `spre`: măsurat pe
  // laturile golului, orice abatere a sensului ar lungi cotele (pe un sit
  // rotit ieșeau 143 × 117 în loc de 130 × 100). `spre` alege doar care latură
  // e adâncimea și încotro e fundul.
  const [ex, ey] = directia(r.poly);
  const [sx, sy] = [s[0] / L, s[1] / L];
  const [px, py] = Math.abs(ex * sx + ey * sy) >= Math.abs(-ey * sx + ex * sy)
    ? [ex, ey] : [-ey, ex];
  const semn = px * sx + py * sy >= 0 ? 1 : -1;
  const [vx, vy] = [px * semn, py * semn];     // spre exterior, pe latura golului
  const [ux, uy] = [vy, -vx];                  // în lungul fațadei
  let [u0, u1, v0, v1] = [Infinity, -Infinity, Infinity, -Infinity];
  for (const [x, y] of r.poly) {
    const u = x * ux + y * uy, v = x * vx + y * vy;
    u0 = Math.min(u0, u); u1 = Math.max(u1, u);
    v0 = Math.min(v0, v); v1 = Math.max(v1, v);
  }
  const la = (m: number) => Math.round(m * 100) / 100;
  const [lu, lv] = [la(u1 - u0), la(v1 - v0)];
  if (!(lu > MIN_COTA_M) || !(lv > MIN_COTA_M)) return [];
  const pt = (u: number, v: number): [number, number] =>
    [u * ux + v * vx, u * uy + v * vy];
  const span = (a: [number, number], b: [number, number], label: string): DimSpan =>
    ({ x1: a[0], y1: a[1], x2: b[0], y2: b[1], label });
  // lanțul, spre fund — dar nu PE fund: acolo, în ultimii 0,20 m, stă scara
  // de pisică (`Ladder`), iar cifrele lanțului o acopereau (30.09.2026)
  const vl = v0 + 0.6 * lv;
  const ua = u0 + 0.3 * lu;      // adâncimea, lângă un perete lateral
  const vt = v1 + e + 0.5;       // totalul, la 0,50 m în afara fundului
  return [
    span(pt(u0 - e, vl), pt(u0, vl), cm(la(e))),
    span(pt(u0, vl), pt(u1, vl), cm(lu)),
    span(pt(u1, vl), pt(u1 + e, vl), cm(la(e))),
    span(pt(ua, v0), pt(ua, v1), cm(lv)),
    span(pt(u0 - e, vt), pt(u1 + e, vt), cm(la(lu + 2 * e))),
  ];
}

/** Sub-cota asta nu se scrie: eticheta ar fi mai lată decât ce descrie, iar un
 *  lanț de cifre suprapuse nu se poate citi. */
const MIN_COTA_M = 0.25;

/** Pragul lanțurilor INTERIOARE, mai mare decât al benzii.
 *
 *  Planșa de referință cotează și grosimile de perete (15, 25, 30) — dar la
 *  1:50 pe hârtie A0, unde o cifră de 25 px are unde să stea. Pe ecran, pe un
 *  etaj cu 468 de pereți, alea sunt câteva sute de cifre de două caractere
 *  înghesuite între altele, iar rezultatul măsurat a fost că se tipăresc una
 *  peste alta: „89" peste „90" citindu-se „8909". O cotă care nu se poate citi
 *  nu e o cotă. Banda perimetrală rămâne la pragul mic: acolo e loc. */
const MIN_INT_M = 0.4;

/** Cât de „dreptunghiulară" trebuie să fie o cameră ca s-o coteze lumina.
 *
 *  Sub pragul ăsta, dreptunghiul orientat din jurul ei e mai mare decât camera,
 *  iar cele două cifre ar descrie o încăpere care nu există — un living în L
 *  cotat 6,10 × 4,70 spune că are 28 m², când el are 22. O cameră neregulată
 *  rămâne NECOTATĂ: aria ei scrisă în plan e măsurată, cotele ar fi ghicite. */
const PRAG_DREPTUNGHI = 0.9;

/** Direcția dominantă a unui inel: versorul laturii celei mai lungi.
 *
 *  Clădirea stă STRÂMB pe lot — pe documentul real, la 86° — deci un
 *  dreptunghi aliniat la axele lumii ar da unei camere de 3×4 o „lumină" de
 *  5×5. Măsurat pe direcția camerei, numărul e cel de pe planșă. */
function directia(inel: number[][]): [number, number] {
  let best = 0, bx = 1, by = 0;
  for (let i = 0; i < inel.length; i++) {
    const a = inel[i], b = inel[(i + 1) % inel.length];
    const dx = b[0] - a[0], dy = b[1] - a[1];
    const L = Math.hypot(dx, dy);
    if (L > best) { best = L; bx = dx / L; by = dy / L; }
  }
  return [bx, by];
}

const ariaInelului = (p: number[][]): number => {
  let s = 0;
  for (let i = 0; i < p.length; i++) {
    const [x1, y1] = p[i], [x2, y2] = p[(i + 1) % p.length];
    s += x1 * y2 - x2 * y1;
  }
  return Math.abs(s) / 2;
};

/** Lumina unei camere, pe direcția ei: două cote prin centru.
 *
 *  Cele două numere pe care planșa de referință le scrie în fiecare cameră
 *  («470», «375 / 610»), și pe care le cere orice verificare de suprafață
 *  minimă. */
function coteleCamerei(r: StoreyShape): DimSpan[] {
  if (r.poly.length < 3) return [];
  const [ux, uy] = directia(r.poly);
  const [vx, vy] = [-uy, ux];
  let [u0, u1, v0, v1] = [Infinity, -Infinity, Infinity, -Infinity];
  for (const [x, y] of r.poly) {
    const u = x * ux + y * uy, v = x * vx + y * vy;
    u0 = Math.min(u0, u); u1 = Math.max(u1, u);
    v0 = Math.min(v0, v); v1 = Math.max(v1, v);
  }
  // La centimetru: documentul scrie colțurile la milimetru, iar o cameră rotită
  // ieșea «130.1» în loc de «130» — zgomot de rotunjire, nu o dimensiune.
  const [lu, lv] = [Math.round((u1 - u0) * 100) / 100, Math.round((v1 - v0) * 100) / 100];
  if (!(lu > MIN_COTA_M) || !(lv > MIN_COTA_M)) return [];
  // Camera trebuie să UMPLE dreptunghiul pe care îl cotăm — v. `PRAG_DREPTUNGHI`.
  if (ariaInelului(r.poly) < PRAG_DREPTUNGHI * lu * lv) return [];
  // Prin centrul dreptunghiului, ca liniile să nu cadă pe pereți.
  const [cu, cv] = [(u0 + u1) / 2, (v0 + v1) / 2];
  const pt = (u: number, v: number): [number, number] =>
    [u * ux + v * vx, u * uy + v * vy];
  const [ax, ay] = pt(u0, cv), [bx, by] = pt(u1, cv);
  const [px, py] = pt(cu, v0), [qx, qy] = pt(cu, v1);
  return [
    { x1: ax, y1: ay, x2: bx, y2: by, label: cm(lu) },
    { x1: px, y1: py, x2: qx, y2: qy, label: cm(lv) },
  ];
}

/** Lanțurile de-a lungul pereților: plin, gol, plin — cum le scrie planșa.
 *
 *  Ăsta e lanțul din planșa de referință («90 / 180 / 105 / 65» de-a lungul unui
 *  perete): cel care spune lățimea unei uși și cât zid rămâne lângă ea.
 *
 *  SE FACE PE ȘIRUL DE PEREȚI, NU PE UN PERETE.
 *
 *  Fiindcă un „perete" din document nu e peretele din desen, ci o BUCATĂ de
 *  perete: generatorul îl taie la fiecare gol, iar golul stă în golul dintre
 *  două bucăți coliniare. Măsurat pe parterul documentului real, distanța de la
 *  centrul unui gol la segmentul lui e EXACT cât îl depășește la capăt — 0,45 m
 *  la uși, 0,90 la ferestre, adică fix o jumătate de gol. Nu e o eroare de
 *  date: e modelul. Bucăți = plinurile, golurile = spațiile dintre ele.
 *
 *  Un lanț făcut pe bucată nu vedea deci NICIUN gol (toate cad în afara ei), și
 *  lanțul ieșea fără nicio lățime de fereastră. Pe șir, cuțitele sunt capetele
 *  bucăților ȘI marginile golurilor, iar între ele iese exact alternanța de pe
 *  planșă.
 *
 *  Ce NU scrie: înălțimea golului și parapetul («210», «hp=110» pe planșă).
 *  `height_m` și `sill_m` sunt `null` în document — pe toate cele 1 800 de
 *  ferestre — deci n-ar fi cote, ar fi invenții. */

/** Cât de departe de linia șirului mai e „pe el". */
const BANDA_M = 0.35;

/** Cheia șirului: direcția (fără sens) și depărtarea liniei de origine, rotunjite.
 *  Două bucăți cu aceeași cheie stau pe aceeași linie. */
function cheiaLiniei(x0: number, y0: number, ux: number, uy: number): string {
  // Fără sens: un perete scris invers e același perete.
  const [dx, dy] = uy < 0 || (uy === 0 && ux < 0) ? [-ux, -uy] : [ux, uy];
  const unghi = Math.round((Math.atan2(dy, dx) * 180) / Math.PI / 1.5) * 1.5;
  // Depărtarea semnată a liniei față de origine, pe normala ei.
  const dep = Math.round((x0 * -dy + y0 * dx) / (BANDA_M / 2)) * (BANDA_M / 2);
  return `${unghi}|${dep.toFixed(2)}`;
}

function lanturile(pereti: StoreyShape[], goluri: StoreyShape[]): DimSpan[] {
  // Bucățile, grupate pe linia pe care stau.
  const siruri = new Map<string, { w: StoreyShape; a: number[]; b: number[] }[]>();
  for (const w of pereti) {
    const ax = w.axis;
    if (!ax || ax.length < 2) continue;
    const a = ax[0], b = ax[ax.length - 1];
    const [dx, dy] = [b[0] - a[0], b[1] - a[1]];
    const L = Math.hypot(dx, dy);
    if (!(L > 0)) continue;
    const k = cheiaLiniei(a[0], a[1], dx / L, dy / L);
    const l = siruri.get(k);
    if (l) l.push({ w, a, b }); else siruri.set(k, [{ w, a, b }]);
  }

  const out: DimSpan[] = [];
  for (const buc of siruri.values()) {
    // Linia șirului: originea și direcția primei bucăți.
    const [ox, oy] = buc[0].a;
    const [ddx, ddy] = [buc[0].b[0] - ox, buc[0].b[1] - oy];
    const LL = Math.hypot(ddx, ddy);
    const [ux, uy] = [ddx / LL, ddy / LL];
    const proj = (p: number[]) => (p[0] - ox) * ux + (p[1] - oy) * uy;
    const perp = (x: number, y: number) => Math.abs((x - ox) * -uy + (y - oy) * ux);

    const taieturi: number[] = [];
    let t0 = Infinity, t1 = -Infinity, gros = 0;
    for (const { w, a, b } of buc) {
      const [pa, pb] = [proj(a), proj(b)];
      taieturi.push(pa, pb);
      t0 = Math.min(t0, pa, pb); t1 = Math.max(t1, pa, pb);
      gros = Math.max(gros, w.thickness_m ?? 0.2);
    }
    // Golurile care stau pe linia asta, oriunde de-a lungul ei.
    let areGol = false;
    for (const o of goluri) {
      const lat = o.width_m ?? 0;
      if (o.cx == null || o.cy == null || !(lat > 0)) continue;
      if (perp(o.cx, o.cy) > BANDA_M) continue;
      const t = proj([o.cx, o.cy]);
      if (t < t0 - lat || t > t1 + lat) continue;
      taieturi.push(t - lat / 2, t + lat / 2);
      t0 = Math.min(t0, t - lat / 2); t1 = Math.max(t1, t + lat / 2);
      areGol = true;
    }
    // Un șir fără niciun gol n-are ce povesti: lungimea lui e deja gabaritul.
    if (!areGol) continue;

    taieturi.sort((a, b) => a - b);
    const dist = gros / 2 + 0.35;
    const [nx, ny] = [-uy * dist, ux * dist];
    for (let i = 1; i < taieturi.length; i++) {
      const [a, b] = [taieturi[i - 1], taieturi[i]];
      if (b - a < MIN_INT_M) continue;
      out.push({
        x1: ox + ux * a + nx, y1: oy + uy * a + ny,
        x2: ox + ux * b + nx, y2: oy + uy * b + ny,
        label: cm(b - a),
      });
    }
  }
  return out;
}

/** E punctul în inel? (ray casting; pe muchie e «înăuntru», ne ajunge) */
function inInel(x: number, y: number, inel: number[][]): boolean {
  let in_ = false;
  for (let i = 0, j = inel.length - 1; i < inel.length; j = i++) {
    const [xi, yi] = inel[i], [xj, yj] = inel[j];
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) in_ = !in_;
  }
  return in_;
}

/** Cât merge raza `(px,py) + t·(dx,dy)` până taie inelul: cea mai mică `t`
 *  peste `tMin`, sau `Infinity`. */
function razaPanaLa(px: number, py: number, dx: number, dy: number,
                    inel: number[][], tMin: number): number {
  let best = Infinity;
  for (let i = 0; i < inel.length; i++) {
    const [ax, ay] = inel[i], [bx, by] = inel[(i + 1) % inel.length];
    const [ex, ey] = [bx - ax, by - ay];
    const den = dx * ey - dy * ex;
    if (Math.abs(den) < 1e-9) continue;
    const t = ((ax - px) * ey - (ay - py) * ex) / den;
    const u = ((ax - px) * dy - (ay - py) * dx) / den;
    if (t > tMin && u >= -1e-9 && u <= 1 + 1e-9 && t < best) best = t;
  }
  return best;
}

/** Cât iese raza din inelul ei: ultima tăietură (raza pleacă din centru). */
function razaIeseDin(px: number, py: number, dx: number, dy: number, inel: number[][]): number {
  let best = 0;
  for (let i = 0; i < inel.length; i++) {
    const [ax, ay] = inel[i], [bx, by] = inel[(i + 1) % inel.length];
    const [ex, ey] = [bx - ax, by - ay];
    const den = dx * ey - dy * ex;
    if (Math.abs(den) < 1e-9) continue;
    const t = ((ax - px) * ey - (ay - py) * ex) / den;
    const u = ((ax - px) * dy - (ay - py) * dx) / den;
    if (t > 0 && u >= -1e-9 && u <= 1 + 1e-9 && t > best) best = t;
  }
  return best;
}

/** Peste atât, distanța dintre un stâlp și un perete nu mai e o cotă de
 *  adăpost, e traveea parcajului — o dă banda `axe`. */
const MAX_ALA_M = 9.0;

/** Corpul unui perete: dreptunghiul axei lui, lat cât grosimea. */
function corpulPeretelui(w: StoreyShape): number[][] | null {
  const ax = w.axis;
  if (!ax || ax.length < 2) return null;
  const [a, b] = [ax[0], ax[ax.length - 1]];
  const L = Math.hypot(b[0] - a[0], b[1] - a[1]);
  if (!(L > 0)) return null;
  const h = (w.thickness_m ?? 0.2) / 2;
  const [nx, ny] = [-(b[1] - a[1]) / L * h, (b[0] - a[0]) / L * h];
  return [[a[0] + nx, a[1] + ny], [b[0] + nx, b[1] + ny],
          [b[0] - nx, b[1] - ny], [a[0] - nx, a[1] - ny]];
}

/** Distanțele dintre pereții unui adăpost și elementele verticale dinăuntru
 *  (Oana, 30.09.2026): din centrul fiecărui stâlp, nucleu, SAS sau grup
 *  sanitar, pe cele două direcții ale adăpostului, de la fața lui până la
 *  primul lucru întâlnit — peretele adăpostului sau alt element. Fiecare
 *  distanță o dată: A → B e și B → A.
 *
 *  PE SPAȚIUL LIBER, între fețele pereților (Oana, 20.10.2026). Conturul
 *  adăpostului (`r.poly`) e tăietura din parcaj, iar peretele de contur stă
 *  ÎNĂUNTRUL ei, cu fața pe contur (`ala/pas.pereti_contur`): o cotă până la
 *  contur lua și cei 0,40 m de beton. La fel SAS-ul: camera lui e lumina, iar
 *  peretele lui de 0,40 stă în afara ei, spre adăpost. De aceea `pereti` —
 *  corpurile pereților adăpostului — sunt obstacole la capăt și se sar la
 *  plecare: raza iese din sursă, trece prin peretele lipit de ea și abia de
 *  acolo măsoară. */
function coteleIntreElemente(r: StoreyShape, plan: StoreyPlan, pereti: StoreyShape[]): DimSpan[] {
  const gauri = r.holes ?? [];
  const corpuri = pereti.map(corpulPeretelui).filter((c): c is number[][] => c !== null);
  const inAdapost = (x: number, y: number) =>
    inInel(x, y, r.poly) && !gauri.some((h) => inInel(x, y, h));
  const centru = (p: number[][]): [number, number] =>
    [p.reduce((s, q) => s + q[0], 0) / p.length, p.reduce((s, q) => s + q[1], 0) / p.length];
  const surse: number[][][] = [];
  for (const c of plan.columns) {
    if (c.poly.length < 3) continue;
    const [x, y] = centru(c.poly);
    if (inAdapost(x, y)) surse.push(c.poly);
  }
  for (const a of plan.spaces) {
    if ((a.kind !== "ala_sas" && a.kind !== "ala_gs") || a.poly.length < 3) continue;
    const [x, y] = centru(a.poly);
    if (inInel(x, y, r.poly)) surse.push(a.poly);
  }
  for (const h of gauri) if (h.length >= 3) surse.push(h);
  if (!surse.length) return coteleCamerei(r);

  const [ux, uy] = directia(r.poly);
  const directii: [number, number][] = [[ux, uy], [-ux, -uy], [-uy, ux], [uy, -ux]];
  const out: DimSpan[] = [];
  // ce s-a cotat deja: axa, lungimea și capătul de jos de-a lungul axei
  const vazute: { k: number; L: number; t: number }[] = [];
  for (const sursa of surse) {
    const [px, py] = centru(sursa);
    for (const [k, [dx, dy]] of directii.entries()) {
      let t0 = razaIeseDin(px, py, dx, dy, sursa);
      // peretele lipit de sursă (al SAS-ului, al grupului sanitar) se sare:
      // cota pleacă de la fața lui, nu de la camera dinăuntru
      for (let pas = 0; pas < 4; pas++) {
        const [qx, qy] = [px + dx * (t0 + 0.005), py + dy * (t0 + 0.005)];
        const c = corpuri.find((c) => inInel(qx, qy, c));
        if (!c) break;
        const t = razaPanaLa(px, py, dx, dy, c, t0 + 0.005);
        if (!Number.isFinite(t)) break;
        t0 = t;
      }
      // Lipit de altă sursă (SAS-ul pe spaletul lui, stâlpul din peretele
      // SAS-ului): între ele nu e nimic de cotat — iar raza, pornind de pe fața
      // ei, îi găsea abia IEȘIREA și scria lumina SAS-ului încă o dată.
      const [lx, ly] = [px + dx * (t0 + 0.005), py + dy * (t0 + 0.005)];
      if (surse.some((alta) => alta !== sursa && inInel(lx, ly, alta))) continue;
      // O BANDĂ cât sursa, nu o rază: doi stâlpi din același șir stau rar pe
      // aceeași axă la centimetru, iar raza din centrul unuia trecea pe lângă
      // celălalt și mergea până la perete — două cote suprapuse pe o linie.
      // Trei raze paralele (centru și marginile sursei) văd tot ce stă în
      // dreptul ei.
      let hp = 0;
      for (const [x, y] of sursa) hp = Math.max(hp, Math.abs((x - px) * -dy + (y - py) * dx));
      const raze = [0, -hp, hp].map((o) => [px - dy * o, py + dx * o]);
      let t1 = Infinity;
      for (const [qx, qy] of raze) {
        t1 = Math.min(t1, razaPanaLa(qx, qy, dx, dy, r.poly, t0 + 1e-6));
        for (const c of corpuri) t1 = Math.min(t1, razaPanaLa(qx, qy, dx, dy, c, t0 + 1e-6));
        for (const alta of surse) {
          if (alta === sursa) continue;
          t1 = Math.min(t1, razaPanaLa(qx, qy, dx, dy, alta, t0 + 1e-6));
        }
      }
      const L = Math.round((t1 - t0) * 100) / 100;
      if (!Number.isFinite(t1) || L < MIN_INT_M || L > MAX_ALA_M) continue;
      const a: [number, number] = [px + dx * t0, py + dy * t0];
      const b: [number, number] = [px + dx * t1, py + dy * t1];
      // ACEEAȘI COTĂ = aceeași axă și același interval de-a lungul ei, oriunde
      // ar sta lateral (Oana, 20.10.2026: «aceeași măsurătoare una lângă
      // alta»). Acoperă A → B / B → A — decalate lateral cu cât nu sunt
      // aliniați, până la jumătatea de lățime a celui mai lat (un SAS de 1,70
      // vedea un stâlp la 0,75 m), iar «mijlocul la sub 0,5 m» le lăsa pe
      // amândouă — și șirul de stâlpi la aceeași depărtare de perete: planșa
      // scrie o dată cât e de la perete la șir, nu la fiecare stâlp.
      const axa = k < 2 ? 0 : 1;
      const [ex, ey] = axa === 0 ? [ux, uy] : [-uy, ux];
      const t = Math.min(a[0] * ex + a[1] * ey, b[0] * ex + b[1] * ey);
      if (vazute.some((v) => v.k === axa && Math.abs(v.L - L) < 0.015
                             && Math.abs(v.t - t) < 0.02)) continue;
      vazute.push({ k: axa, L, t });
      out.push({ x1: a[0], y1: a[1], x2: b[0], y2: b[1], label: cm(L) });
    }
  }
  return out;
}

/** Cotele adăposturilor ALA — v. `CoteNivel.ala`. */
function coteleAdaposturilor(plan: StoreyPlan): DimSpan[] {
  const adaposturi = plan.spaces.filter((r) => r.kind === "ala" && r.poly.length >= 3);
  if (!adaposturi.length) return [];
  const out: DimSpan[] = [];
  // SAS-ul și grupul sanitar: lățimea și lungimea lor
  for (const a of plan.spaces) {
    if (a.kind === "ala_sas" || a.kind === "ala_gs") out.push(...coteleCamerei(a));
  }
  // lățimea ușilor și a oblonului, cu plinurile de lângă ele, pe pereții
  // adăpostului (nu și pe cei ai puțului: el are cotele lui, `iesiri`).
  // PE FIECARE ADĂPOST ÎN PARTE (Oana, 30.09.2026): pereții de contur a două
  // adăposturi alăturate stau pe aceeași linie, iar un lanț pe toți pereții
  // ALA trecea dintr-un adăpost în vecinul lui. Peretele comun («ALA 1 / ALA
  // 2») intră în lanțul fiecăruia.
  for (const r of adaposturi) {
    const eticheta = r.ala?.adapost;
    if (!eticheta) continue;
    const alLui = (a?: string) => !!a && a.split(" / ").includes(eticheta);
    const pereti = plan.walls.filter((w) => !!w.ala && alLui(w.ala.adapost));
    out.push(...lanturile(
      pereti.filter((w) => w.ala!.rol !== "saritura"),
      plan.openings.filter((o) => !!o.ala && alLui(o.ala.adapost))));
    out.push(...coteleIntreElemente(r, plan, pereti));
  }
  return out;
}

/** Inelul fără zgomot: vârfurile la sub 2 cm de cel dinainte și cele
 *  coliniare pleacă. Miezul holului vine din `buffer` peste o axă cu colțuri
 *  rotunjite la milimetru și are vârfuri dublate la centimetru
 *  (`[583046.99, …], [583047.02, …], [583047.02, …]`), care ar tăia o latură
 *  de 4,70 în trei cote fără sens. */
function inelCurat(inel: number[][]): number[][] {
  const out: number[][] = [];
  for (const p of inel) {
    const q = out[out.length - 1];
    if (q && Math.hypot(p[0] - q[0], p[1] - q[1]) < 0.02) continue;
    out.push(p);
  }
  while (out.length > 1 && Math.hypot(out[0][0] - out[out.length - 1][0], out[0][1] - out[out.length - 1][1]) < 0.02) out.pop();
  if (out.length < 3) return out;
  const curat: number[][] = [];
  for (let i = 0; i < out.length; i++) {
    const a = out[(i + out.length - 1) % out.length], b = out[i], c = out[(i + 1) % out.length];
    const [ux, uy] = [b[0] - a[0], b[1] - a[1]], [vx, vy] = [c[0] - b[0], c[1] - b[1]];
    const cross = Math.abs(ux * vy - uy * vx) / (Math.hypot(ux, uy) * Math.hypot(vx, vy));
    if (cross < 0.02 && ux * vx + uy * vy > 0) continue;        // coliniar, același sens
    curat.push(b);
  }
  return curat;
}

/** Cât de departe de fața peretelui stă cota holului, înăuntrul lui. */
const COTA_HOL_M = 0.15;
/** Lumina (laturile de capăt, până la atât) stă mai adânc în hol: pe capăt e
 *  ușa, iar cifra se tipărea peste foaia ei și peste «EI 60-C» (20.10.2026).
 *  La un metru, dincolo de bătaia ușii de 0,90 — sau la 40 % din laturile
 *  vecine, pe un hol mai scurt. */
const LUMINA_HOL_M = 1.2;
const COTA_LUMINA_M = 1.0;

/** Cotele holurilor de evacuare — v. `CoteNivel.evacuare`.
 *
 *  Fiecare latură a miezului (lumina holului) e o cotă: capetele de 0,90 dau
 *  lățimea liberă, laturile lungi dau lungimea fiecărei bucăți drepte — pe un
 *  hol în L, și cea dinafară, și cea dinăuntru, cum le scrie planșa. O lungime
 *  o singură dată pe hol: aceeași direcție, același interval de-a lungul ei,
 *  oriunde ar sta lateral (cele două fețe ale unui hol drept, cele două capete
 *  ale lui, 20.10.2026). Cota stă înăuntrul holului, la `COTA_HOL_M` de față. */
function coteleHolurilor(plan: StoreyPlan): DimSpan[] {
  const out: DimSpan[] = [];
  for (const r of plan.spaces.filter(eHolEvacuare)) {
    const inel = inelCurat(r.poly);
    if (inel.length < 3) continue;
    let aria2 = 0;
    for (let i = 0; i < inel.length; i++) {
      const [x1, y1] = inel[i], [x2, y2] = inel[(i + 1) % inel.length];
      aria2 += x1 * y2 - x2 * y1;
    }
    const semn = aria2 >= 0 ? 1 : -1;        // înăuntru = la stânga unei laturi parcurse pozitiv
    const vazute: { unghi: number; t: number; L: number }[] = [];
    const n = inel.length;
    const lung = (i: number) => Math.hypot(inel[(i + 1) % n][0] - inel[i][0], inel[(i + 1) % n][1] - inel[i][1]);
    for (let i = 0; i < n; i++) {
      const a = inel[i], b = inel[(i + 1) % n];
      const [dx, dy] = [b[0] - a[0], b[1] - a[1]];
      const L0 = Math.hypot(dx, dy);
      const L = Math.round(L0 * 100) / 100;
      if (!(L >= MIN_COTA_M)) continue;
      const [ux, uy] = [dx / L0, dy / L0];
      const adanc = L <= LUMINA_HOL_M
        ? Math.max(COTA_HOL_M, Math.min(COTA_LUMINA_M, 0.4 * Math.min(lung((i + n - 1) % n), lung((i + 1) % n))))
        : COTA_HOL_M;
      // direcția fără sens și intervalul de-a lungul ei
      const [ex, ey] = uy < 0 || (uy === 0 && ux < 0) ? [-ux, -uy] : [ux, uy];
      const unghi = Math.round((Math.atan2(ey, ex) * 180) / Math.PI);
      const t = Math.min(a[0] * ex + a[1] * ey, b[0] * ex + b[1] * ey);
      if (vazute.some((v) => Math.abs(v.unghi - unghi) <= 1 && Math.abs(v.L - L) < 0.015
                             && Math.abs(v.t - t) < 0.02)) continue;
      vazute.push({ unghi, t, L });
      const [nx, ny] = [-uy * semn * adanc, ux * semn * adanc];
      out.push({ x1: a[0] + nx, y1: a[1] + ny, x2: b[0] + nx, y2: b[1] + ny, label: cm(L) });
    }
  }
  return out;
}

/** Cotele unui nivel, măsurate din geometria lui.
 *
 *  Conturul dă gabaritul; stâlpii dau traveele. Un nivel fără contur n-are de
 *  unde: nu se cotează bounding-box-ul camerelor, fiindcă aia ar fi cota a ce
 *  s-a desenat, nu a ce e clădirea — și pe un planșeu cu o cameră lipsă ar
 *  ieși un număr care pare măsurat și nu e. */
/** CADRUL CLĂDIRII: originea și versorii ei, nu ai lumii.
 *
 *  Clădirea stă strâmb pe lot — 4° pe corpul C3 al documentului real — și tot
 *  ce se cotează perimetral trebuie măsurat pe direcția EI. Măsurat pe axele
 *  lumii, corpul C3 ieșea 78,26 × 45,96 m; pe direcția lui e 75,30 × 45,00.
 *  Prima pereche descrie plicul rotit, nu clădirea, iar cotele ieșeau oblice.
 *
 *  Direcția o dă latura cea mai lungă a conturului: fațada principală. */
function cadrul(inel: number[][]) {
  let best = 0, ux = 1, uy = 0;
  for (let i = 0; i < inel.length; i++) {
    const a = inel[i], b = inel[(i + 1) % inel.length];
    const L = Math.hypot(b[0] - a[0], b[1] - a[1]);
    if (L > best) { best = L; ux = (b[0] - a[0]) / L; uy = (b[1] - a[1]) / L; }
  }
  const [ox, oy] = inel[0];
  const u = (x: number, y: number) => (x - ox) * ux + (y - oy) * uy;
  const v = (x: number, y: number) => -(x - ox) * uy + (y - oy) * ux;
  const xy = (a: number, b: number): [number, number] =>
    [ox + a * ux - b * uy, oy + a * uy + b * ux];
  let u0 = Infinity, u1 = -Infinity, v0 = Infinity, v1 = -Infinity;
  for (const [x, y] of inel) {
    const [a, b] = [u(x, y), v(x, y)];
    u0 = Math.min(u0, a); u1 = Math.max(u1, a);
    v0 = Math.min(v0, b); v1 = Math.max(v1, b);
  }
  return { u, v, xy, u0, u1, v0, v1 };
}

type Cadru = ReturnType<typeof cadrul>;

/** Cât de departe de clădire stau cele trei rânduri, în metri de desen.
 *  Proporțiile sunt cele de pe A07 (47 / 66 / 78 px la conturul ei). */
const RAND_M = [1.1, 2.5, 3.9];

/** Un rând de bandă: pozițiile date, așezate pe o dreaptă paralelă cu latura.
 *
 *  `peU` spune dacă rândul se citește de-a lungul lui u (latura „de sus") sau
 *  de-a lungul lui v (latura „din stânga"). Banda e DREAPTĂ pe toată latura,
 *  cum o desenează planșa: nu urmărește retragerile și nu ocolește balcoanele. */
function rand(c: Cadru, poz: number[], la: number, peU: boolean): DimSpan[] {
  const out: DimSpan[] = [];
  for (let i = 1; i < poz.length; i++) {
    const [a, b] = [poz[i - 1], poz[i]];
    if (b - a < MIN_COTA_M) continue;
    const [p, q] = peU ? [c.xy(a, la), c.xy(b, la)] : [c.xy(la, a), c.xy(la, b)];
    out.push({ x1: p[0], y1: p[1], x2: q[0], y2: q[1], label: cm(b - a) });
  }
  return out;
}

/** Reperele de pe o fațadă: capetele bucăților de perete și marginile golurilor
 *  care stau pe ea. Adică exact ce cotează rândul lipit de clădire pe A07. */
function repereFatada(plan: StoreyPlan, c: Cadru, peU: boolean,
                      laExtrem: number): number[] {
  const dincolo = (x: number, y: number) => (peU ? c.v(x, y) : c.u(x, y));
  const dea = (x: number, y: number) => (peU ? c.u(x, y) : c.v(x, y));
  // «Pe fațadă» = la cel mult un metru de fața extremă. Un metru fiindcă
  // peretele are grosime și axa lui stă înăuntru; mai mult ar trage în bandă și
  // primul perete interior, care are lanțul lui.
  const PRAG = 1.0;
  const out: number[] = [];
  for (const w of plan.walls) {
    const ax = w.axis;
    if (!ax || ax.length < 2) continue;
    const [a, b] = [ax[0], ax[ax.length - 1]];
    if (Math.abs(dincolo(a[0], a[1]) - laExtrem) > PRAG) continue;
    if (Math.abs(dincolo(b[0], b[1]) - laExtrem) > PRAG) continue;
    out.push(dea(a[0], a[1]), dea(b[0], b[1]));
  }
  for (const o of plan.openings) {
    const lat = o.width_m ?? 0;
    if (o.cx == null || o.cy == null || !(lat > 0)) continue;
    if (Math.abs(dincolo(o.cx, o.cy) - laExtrem) > PRAG) continue;
    const t = dea(o.cx, o.cy);
    out.push(t - lat / 2, t + lat / 2);
  }
  return pozitii(out).map((p) => p.la);
}

/** Cotele unui nivel, măsurate din geometria lui.
 *
 *  Conturul dă cadrul și totalul; fațadele dau lanțul fin; stâlpii dau
 *  aliniamentele; camerele și pereții dinăuntru dau cotele interioare. Un nivel
 *  fără contur nu se cotează deloc: n-ar avea în ce cadru s-o facă. */
export function coteNivel(plan: StoreyPlan): CoteNivel {
  if (plan.outline.length < 3) return CoteGoale;
  const c = cadrul(plan.outline);

  const centre = plan.columns.map(centrul)
    .filter((p): p is [number, number] => p !== null)
    .map(([x, y]): [number, number] => [c.u(x, y), c.v(x, y)])
    .filter(([a, b]) => a >= c.u0 - TOL_M && a <= c.u1 + TOL_M
                     && b >= c.v0 - TOL_M && b <= c.v1 + TOL_M);
  const aliniate = (vals: number[]) => pozitii(vals)
    .filter((p) => p.cati >= MIN_ALINIERE).map((p) => p.la);

  const laturi = [
    { peU: true,  ext: c.v0, a0: c.u0, a1: c.u1,
      ax: aliniate(centre.map((p) => p[0])) },
    { peU: false, ext: c.u0, a0: c.v0, a1: c.v1,
      ax: aliniate(centre.map((p) => p[1])) },
  ];

  const fatada: DimSpan[] = [];
  const axe: DimSpan[] = [];
  const total: DimSpan[] = [];
  for (const l of laturi) {
    // Rândurile stau pe partea DINAFARA clădirii, deci la coordonate mai mici
    // decât fața ei — de-aia se scad.
    const la = (i: number) => l.ext - RAND_M[i];
    fatada.push(...rand(c, repereFatada(plan, c, l.peU, l.ext), la(0), l.peU));
    if (l.ax.length >= 2) axe.push(...rand(c, l.ax, la(1), l.peU));
    if (l.a1 - l.a0 > MIN_COTA_M) {
      total.push(...rand(c, [l.a0, l.a1], la(2), l.peU));
    }
  }

  // INTERIORUL: pereții de fațadă sunt deja cotați de bandă, deci nu intră aici.
  // Desenați de două ori, ei erau jumătate din îngrămădeala de pe ecran.
  const interiori = plan.walls.filter((w) => {
    // pereții adăposturilor ALA au lanțurile lor (`ala`), scrise și când stau pe fațadă
    if (w.ala) return false;
    const ax = w.axis;
    if (!ax || ax.length < 2) return true;
    const [a, b] = [ax[0], ax[ax.length - 1]];
    for (const l of laturi) {
      const d = (p: number[]) => (l.peU ? c.v(p[0], p[1]) : c.u(p[0], p[1]));
      if (Math.abs(d(a) - l.ext) <= 1.0 && Math.abs(d(b) - l.ext) <= 1.0) {
        return false;
      }
    }
    return true;
  });

  const peretiPut = plan.walls.filter((w) => w.ala?.rol === "saritura");
  return {
    fatada, axe, total,
    camere: plan.spaces.filter((r) => !eIesireAla(r) && !eCameraAla(r) && !eHolEvacuare(r)).flatMap(coteleCamerei),
    iesiri: plan.spaces.filter(eIesireAla)
      .flatMap((r) => coteleIesirii(r, grosimeaPutului(r, peretiPut))),
    goluri: lanturile(interiori, plan.openings),
    ala: coteleAdaposturilor(plan),
    evacuare: coteleHolurilor(plan),
  };
}

/** Câte cote are nivelul — numărul pe care îl arată bifa. */
export const numaraCote = (c: CoteNivel) =>
  c.fatada.length + c.axe.length + c.total.length
  + c.camere.length + c.iesiri.length + c.goluri.length + c.ala.length + c.evacuare.length;
